'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

export function useVerticalScrollCarousel(
  containerRef: React.RefObject<HTMLDivElement | null>,
  stickyContainerRef: React.RefObject<HTMLDivElement | null>,
  initialHeight: number,
  stepLength: number
) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stickyContainerHeight, setStickyContainerHeight] = useState(initialHeight);
  const rafRef = useRef<number | null>(null);

  const updateFromScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const scrolled = -rect.top;
    const totalScrollable = stickyContainerHeight * (stepLength - 1);

    if (scrolled <= 0) {
      setActiveIndex(0);
      setProgress(0);
    } else if (scrolled >= totalScrollable) {
      setActiveIndex(stepLength - 1);
      setProgress(100);
    } else {
      const idx = Math.floor(scrolled / stickyContainerHeight);
      const prog = ((scrolled % stickyContainerHeight) / stickyContainerHeight) * 100;
      setActiveIndex(idx);
      setProgress(prog);
    }
  }, [containerRef, stickyContainerHeight, stepLength]);

  useEffect(() => {
    const scheduleUpdate = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        updateFromScroll();
      });
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    scheduleUpdate();

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [updateFromScroll]);

  useEffect(() => {
    if (stickyContainerRef.current) {
      setStickyContainerHeight(stickyContainerRef.current.offsetHeight);
    }
  }, [stickyContainerRef]);

  const handleItemClick = (index: number) => {
    const container = containerRef.current;
    if (!container) return;

    const containerTop = container.getBoundingClientRect().top + window.scrollY;
    const targetScroll = containerTop + index * stickyContainerHeight;
    window.scrollTo({ top: targetScroll, behavior: 'smooth' });
  };

  return {
    activeIndex,
    progress,
    containerRef,
    handleItemClick,
    stickyContainerHeight,
    totalHeight: stickyContainerHeight * stepLength,
  };
}
